import { BaseEntity } from './base-entity';
import { ColorToken } from './topic';
import { DropdownItem } from './form/basic';
import { LabelType, TranslateFunction } from '@angular-slider/ngx-slider';
import { ComparisonValue } from './geo-data';
import {
  ChartMode,
  ChartSorting,
  LegendSize,
  ScaleMode,
} from '../services/grid-widget-config.service';

export type MapLegendScope = 'group' | 'all';
export type MapLegendPosition =
  | 'topleft'
  | 'topright'
  | 'bottomleft'
  | 'bottomright';
export interface Commune extends BaseEntity {
  slug: string;
  name: string;
  title: string;
  key: string;
  street: string;
  zip: string;
  city: string;
  locked: boolean;
  contactPersonSalutation: string;
  contactPersonLastName: string;
  contactPersonFirstName: string;
  contactPersonPhone: string;
  contactPersonMail: string;
  contactPersonRole: string;
  organisation: string;
  logo: string;
  defaultBookmark: string;
  defaultBookmarkExternal: string;
  atlasConfig: AtlasConfiguration;
  contactUrl: string;
  thematic: string;
  twoFactorAuth: boolean;
}

export type MapLegend = {
  invert: boolean;
  legendScope: MapLegendScope;
  numberOfScales: number;
  scaleMode: ScaleMode;
  size: LegendSize;
  position: string;
};

export interface MapConfiguration {
  tileLayer: string;
  captions: {
    key: boolean;
    name: boolean;
    value: boolean;
    chart: boolean;
    style: string;
  };
  colorset?: string;
  showChart: boolean;
  hideKaValues: boolean;
  direction: 'auto' | 'left' | 'right' | 'center' | 'no-background';
  legendScope: 'all' | 'group';
  legend: MapLegend;
  districtOpacity: number;
  strokeColor: string;
  poiSize: number;
  emptyFillColor: string;
}

export interface BarChartConfiguration {
  displayValues: boolean;
  sort: ChartSorting;
  caption: 'key' | 'name';
  colorset: string;
  mode: ChartMode;
  stacked: boolean;
}

export interface ScatterConfiguration {
  displayRegression: boolean;
  dotSize: number;
}

export interface BubbleConfiguration {
  displayRegression: boolean;
}

export interface LineConfiguration {
  strokeWidth: number;
  lineTension: number;
  dotSize: number;
  colorset?: string;
}

export interface TableConfiguration {
  displayKeys: boolean;
  captionTotal: string;
  captionAverage: string;
  captionDistrict: string;
  captionKey: string;
  transpose: boolean;
  sort: ChartSorting;
}

export interface LandingPageItem {
  id: string;
  name: string;
  type: string;
  text?: string;
  icon?: string;
  spotlight?: boolean;
  color?: ColorToken;
}

export interface FallbackTopicConfiguration {
  title: string;
  icon: string;
  visible: boolean;
  spotlight: boolean;
  description?: string;
}

export interface PieConfiguration {
  colorset?: string;
  displayValues: boolean;
  innerRadius: number;
  sort: ChartSorting;
  name: string;
}

export interface RadarConfiguration {
  colorset?: string;
  displayValues: boolean;
  dotSize: number;
  featureOpacity: number;
  lineTension: number;
}

export interface PolarConfiguration {
  colorset?: string;
  displayValues: boolean;
  //sort: ChartSorting;
}

export interface AtlasConfiguration {
  headline: string;
  logo: string;
  colorset?: string;
  startTeaser: string;
  absoluteDecimals: number;
  relativeDecimals: number;
  miscValues: ComparisonValue[];
  map: MapConfiguration;
  bar: BarChartConfiguration;
  line: LineConfiguration;
  scatter: ScatterConfiguration;
  pie: PieConfiguration;
  polarArea: PolarConfiguration;
  bubble: BubbleConfiguration;
  radar: RadarConfiguration;
  table: TableConfiguration;
  adder: any;
  text: any;
  teaser: any;
  info: any;
  landingPageItems: LandingPageItem[];
  navigation: string[];
  allowWegweiserData: 'all' | 'external' | 'internal' | 'none';
  showIndicatorNumbers?: boolean;
  fallbackTopic?: FallbackTopicConfiguration;
}

export const leafletLabelDirectionOptions: DropdownItem[] = [
  {
    label: 'automatisch',
    value: 'auto',
  },
  {
    label: 'links',
    value: 'left',
  },
  {
    label: 'rechts',
    value: 'right',
  },
  {
    label: 'zentriert',
    value: 'center',
  },
  {
    label: 'ohne Hintergrund',
    value: 'no-background',
  },
];

export const leafletTileLayerOptions: DropdownItem[] = [
  {
    label: 'Graustufen',
    value: 'light',
  },
  {
    label: 'Farbig',
    value: 'traffic',
  },
  {
    label: 'Straßen (farbig)',
    value: 'streets',
  },
  {
    label: 'Satellit',
    value: 'satellite',
  },
  //{
  //  label: 'Dunkel',
  //  value: 'dark',
  //},
  {
    label: 'kein Hintergrund',
    value: 'blank',
  },
];

export const leafletCaptionOptions: DropdownItem[] = [
  {
    label: 'Schlüssel',
    value: 'key',
  },
  {
    label: 'Name',
    value: 'name',
  },
  {
    label: 'Wert',
    value: 'value',
  },
];

export const chartSortingOptions: DropdownItem[] = [
  {
    label: 'Schlüssel',
    value: 'key',
  },
  {
    label: 'Name',
    value: 'name',
  },
  {
    label: 'Wert aufsteigend',
    value: 'value-asc',
  },
  {
    label: 'Wert absteigend',
    value: 'value-desc',
  },
];

export const strokeColors: any = [
  {
    value: '#ffffff',
  },
  {
    value: '#eeeeee',
  },
  {
    value: '#dddddd',
  },
  {
    value: '#cccccc',
  },
  {
    value: '#bbbbbb',
  },
  {
    value: '#aaaaaa',
  },
  {
    value: '#999999',
  },
  {
    value: '#888888',
  },
  {
    value: '#777777',
  },
  {
    value: '#666666',
  },
  {
    value: '#555555',
  },
  {
    value: '#444444',
  },
  {
    value: '#333333',
  },
  {
    value: '#222222',
  },
  {
    value: '#111111',
  },
  {
    value: '#000000',
  },
];

export const hexToFas: TranslateFunction = (
  value: number,
  label: LabelType
): string => {
  const color: string = strokeColors[value]?.value ?? '#ffffff';
  return '<i class="fas fa-circle" style="color:' + color + '"></i>';
};
